import type { CreditTransaction, UsageKind } from '@/lib/api'

export interface UsageLogRow {
  date: string
  user: string
  type: UsageKind
  project: string
  credits: number
  costUsd: number
}

const TYPE_LABEL: Record<UsageKind, string> = {
  audience: 'Audience',
  study: 'Study',
  chat: 'Chat',
  social: 'Social listening',
  system: 'System',
}

const ESCAPE_RE = /[",\n\r]/

function csvEscape(value: string): string {
  if (ESCAPE_RE.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export function rowsToCsv(rows: UsageLogRow[]): string {
  const header = ['Date', 'User', 'Type', 'Project', 'Credits', 'Cost (USD)']
  const lines = [header.join(',')]
  for (const r of rows) {
    lines.push(
      [
        csvEscape(r.date),
        csvEscape(r.user),
        csvEscape(TYPE_LABEL[r.type]),
        csvEscape(r.project),
        r.credits.toFixed(2),
        r.costUsd.toFixed(2),
      ].join(','),
    )
  }
  return lines.join('\n')
}

/**
 * Map a `CreditTransaction` row to a `UsageLogRow` ready for export. Refunds
 * (`type: 'credit'`) are surfaced with negative credits so totals reconcile.
 */
function usageActorLabel(
  tx: CreditTransaction,
  viewerFallback: string,
  currentUserId?: string,
): string {
  if (tx.performedByDisplayName) {
    return tx.performedByDisplayName
  }
  if (
    tx.performedByUserId &&
    currentUserId &&
    tx.performedByUserId === currentUserId
  ) {
    return viewerFallback
  }
  if (tx.performedByUserId) {
    return 'Unknown user'
  }
  return viewerFallback
}

export function txToUsageRow(
  tx: CreditTransaction,
  costPerCreditCents: number,
  opts: { viewerFallback: string; currentUserId?: string },
): UsageLogRow {
  const credits = tx.type === 'debit' ? tx.amount : -tx.amount
  const costUsd = (credits * costPerCreditCents) / 100
  return {
    date: tx.createdAt,
    user: usageActorLabel(tx, opts.viewerFallback, opts.currentUserId),
    type: tx.kind,
    project: tx.description,
    credits,
    costUsd,
  }
}

export function downloadCsv(filename: string, csv: string): void {
  if (typeof window === 'undefined') return
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Credit metering: the whole range of the daily usage log as CSV, built by the API (token
 * types, rates, version, accrued and posted), downloaded as-is. No pagination, no client math.
 */
export async function downloadDailyUsageCsv(
  fetchCsv: (params: { from: string; to: string; scope: 'me' | 'team' }) => Promise<string>,
  range: { from: string; to: string; scope: 'me' | 'team' },
): Promise<void> {
  const csv = await fetchCsv(range)
  downloadCsv(`usage-log-${range.from}-${range.to}.csv`, csv)
}
